import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Plus, Edit, Trash2, X, Save, Calendar, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyPost = {
  title: '',
  cover_image: '',
  excerpt: '',
  content: '',
  author: 'The Navkar Travels',
  date: '',
  published: true,
};

const AdminBlog = () => {
  const { adminAuth } = useOutletContext();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const authHeaders = {
    Authorization: `Basic ${adminAuth}`,
    'Content-Type': 'application/json',
  };

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blog`, {
        headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) {
        setPosts(await res.json());
      }
    } catch (e) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const openCreate = () => {
    setForm({ ...emptyPost, date: new Date().toISOString().slice(0, 10) });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setForm({
      title: post.title || '',
      cover_image: post.cover_image || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author: post.author || 'The Navkar Travels',
      date: post.date || '',
      published: post.published !== false,
    });
    setEditingId(post.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyPost);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${API_URL}/api/admin/blog/${editingId}` : `${API_URL}/api/admin/blog`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
      if (res.ok) {
        toast.success(editingId ? 'Post updated' : 'Post created');
        closeForm();
        fetchPosts();
      } else {
        toast.error('Failed to save post');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) {
        toast.success('Post deleted');
        fetchPosts();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Connection error');
    }
  };

  const getFullUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_URL}${url}`);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Travel Blog</h1>
          <p className="text-gray-600">Create and manage blog posts</p>
        </div>
        {!showForm && (
          <Button onClick={openCreate} data-testid="new-post-btn" className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        )}
      </div>

      {showForm ? (
        <Card data-testid="blog-form">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{editingId ? 'Edit Post' : 'New Post'}</CardTitle>
              <button onClick={closeForm} className="p-2 text-gray-500 hover:bg-gray-100 rounded" data-testid="close-form-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input data-testid="post-title-input" value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Post title" />
            </div>

            <ImageUploader
              value={form.cover_image}
              onChange={(url) => setField('cover_image', url)}
              uploadEndpoint="/api/admin/upload/blog"
              adminAuth={adminAuth}
              label="Cover Image"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Author</label>
                <Input data-testid="post-author-input" value={form.author} onChange={(e) => setField('author', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <Input type="date" data-testid="post-date-input" value={form.date} onChange={(e) => setField('date', e.target.value)} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Excerpt (short summary)</label>
              <Textarea data-testid="post-excerpt-input" rows={2} value={form.excerpt} onChange={(e) => setField('excerpt', e.target.value)} placeholder="A short teaser shown on the blog list" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <RichTextEditor value={form.content} onChange={(html) => setField('content', html)} adminAuth={adminAuth} />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => setField('published', v)} data-testid="post-published-switch" />
              <span className="text-sm text-gray-700">{form.published ? 'Published (visible on site)' : 'Draft (hidden)'}</span>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-post-btn" className="bg-amber-600 hover:bg-amber-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Post'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No blog posts yet</h3>
                <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" /> Create Your First Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            posts.map((post) => (
              <Card key={post.id} data-testid={`admin-blog-row-${post.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {post.cover_image && (
                      <img src={getFullUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${post.published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{post.excerpt}</p>
                    <div className="flex items-center text-xs text-gray-400 mt-1">
                      <Calendar className="w-3 h-3 mr-1" /> {post.date} · /{post.slug}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(post)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-post-${post.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(post.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-post-${post.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
